import { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge, Box, Flex, HStack, Spinner, Text, VStack, useColorModeValue,
} from "@chakra-ui/react";
import {
  Banknote, Briefcase, Building2, Calendar, CheckCircle, Clock, History,
  Inbox, ListChecks, MapPin, Pencil, User, UserCog, UserMinus, UserPlus,
  XCircle, type LucideIcon,
} from "lucide-react";
import { keyframes } from "@emotion/react";

import { getHrAuditFeed, type HrProcessAuditEntry } from "../../../../lib/api";
import {
  AUDIT_VERB, candidateDisplayName, describeAuditParts, formatDateTime, type AuditChangeType,
} from "../hrAuditLabels";
import { OnflyLoading } from "../../../../tools/OnflyLoading";

const PAGE_SIZE = 5;

export interface HrAuditLogPageProps {
  token: string;
  /** Incrementado pelo pai para forçar re-fetch (ex.: após editar no drawer). */
  refreshKey: number;
  /** Filtro textual (vindo do SideFilter do pai). */
  search: string;
  /** Período (vindo do SideFilter do pai), em ISO yyyy-mm-dd. */
  dateFrom?: string;
  dateTo?: string;
  /** E-mails de candidatos selecionados no filtro (vindo do SideFilter do pai). */
  candidate: string[];
}

// ─── Mapeamento field/changeType → ícone + cor semântica ───────────────────────

type Tone = "blue" | "red" | "green" | "purple";

interface AuditVisual {
  Icon: LucideIcon;
  tone: Tone;
}

const TRUTHY = new Set(["true", "sim", "1", "yes", "completed", "concluido", "concluído", "feito"]);

const isTruthy = (v: string | null): boolean => v !== null && TRUTHY.has(v.toLowerCase().trim());

const FIELD_ICON: Record<string, LucideIcon> = {
  salary: Banknote,
  job_title: Briefcase,
  job_activities: ListChecks,
  cost_center: Building2,
  admission_date: Calendar,
  work_start_time: Clock,
  work_end_time: Clock,
  work_schedule: Clock,
  workplace: MapPin,
};

const TONE_BY_CHANGE: Record<AuditChangeType, Tone> = {
  added: "blue",
  changed: "purple",
  removed: "red",
  cleared: "red",
};

function getAuditVisual(fieldKey: string, changeType: AuditChangeType, rawNew: string | null): AuditVisual {
  if (fieldKey === "admission_exam_completed") {
    return isTruthy(rawNew)
      ? { Icon: CheckCircle, tone: "green" }
      : { Icon: XCircle, tone: "red" };
  }
  if (fieldKey === "recruiter" || fieldKey === "responsible_manager") {
    if (changeType === "added") return { Icon: UserPlus, tone: "blue" };
    if (changeType === "removed" || changeType === "cleared") return { Icon: UserMinus, tone: "red" };
    return { Icon: UserCog, tone: "purple" };
  }
  const Icon = FIELD_ICON[fieldKey] ?? Pencil;
  return { Icon, tone: TONE_BY_CHANGE[changeType] };
}

// ─── Animação discreta de entrada ──────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Component ────────────────────────────────────────────────────────────────

/** Aba "Histórico": timeline com scroll infinito (5 em 5, estilo logs do Cloud Run)
 * de alterações em dados sensíveis do RH. Busca, período e candidato vêm do SideFilter;
 * a paginação é feita no back (page/page_size) e acumulada aqui no front. */
export const HrAuditLogPage = ({ token, refreshKey, search, dateFrom, dateTo, candidate }: HrAuditLogPageProps) => {
  const [entries, setEntries] = useState<HrProcessAuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Impede disparar vários "load more" concorrentes.
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(false);

  // Tokens de cor ── superfície
  const heading       = useColorModeValue("gray.800", "gray.100");
  const muted         = useColorModeValue("gray.500", "gray.400");
  const label         = useColorModeValue("gray.700", "gray.200");
  const accent        = useColorModeValue("blue.600", "blue.300");
  const surfaceBg     = useColorModeValue("#f5f5f5", "#0a1628");
  const cardBg        = useColorModeValue("white", "#0f2040");
  const cardBorder    = useColorModeValue("gray.200", "whiteAlpha.100");
  const cardHoverBg   = useColorModeValue("gray.50", "whiteAlpha.50");
  const cardHoverBdr  = useColorModeValue("gray.300", "whiteAlpha.200");
  const lineColor     = useColorModeValue("gray.200", "whiteAlpha.100");
  const nodeRing      = useColorModeValue("rgba(0,0,0,0.05)", "rgba(255,255,255,0.06)");

  // Tokens de cor ── header / empty
  const headerIconBg  = useColorModeValue("blue.50", "rgba(0,158,251,0.12)");
  const headerIconClr = useColorModeValue("blue.500", "blue.300");
  const emptyBg        = useColorModeValue("gray.100", "whiteAlpha.100");
  const emptyClr       = useColorModeValue("gray.400", "gray.500");

  // Tokens de cor ── nós da timeline (4 tons fixos → hooks estáveis)
  const toneColors: Record<Tone, { bg: string; icon: string }> = {
    blue:   { bg: useColorModeValue("blue.50", "rgba(0,158,251,0.14)"),   icon: useColorModeValue("blue.500", "blue.300") },
    red:    { bg: useColorModeValue("red.50", "rgba(254,178,178,0.14)"),   icon: useColorModeValue("red.500", "red.300") },
    green:  { bg: useColorModeValue("green.50", "rgba(72,187,120,0.14)"),  icon: useColorModeValue("green.500", "green.300") },
    purple: { bg: useColorModeValue("purple.50", "rgba(159,122,234,0.14)"),icon: useColorModeValue("purple.500", "purple.300") },
  };

  const candChipBg  = useColorModeValue("blue.50", "rgba(0,158,251,0.12)");
  const candChipClr = useColorModeValue("blue.700", "blue.200");

  const hasFilters = !!(search || dateFrom || dateTo || candidate.length > 0);

  /** Primeira carga (ou recarga ao mudar filtros): reseta tudo e busca a página 1. */
  const loadFirst = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getHrAuditFeed(token, 1, PAGE_SIZE, search || undefined, dateFrom, dateTo, candidate);
      setEntries(res.entries);
      setTotal(res.total);
      setPage(1);
      const more = res.entries.length < res.total;
      setHasMore(more);
      hasMoreRef.current = more;
    } catch {
      setEntries([]);
      setTotal(0);
      setHasMore(false);
      hasMoreRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [token, search, dateFrom, dateTo, candidate]);

  useEffect(() => { void loadFirst(); }, [loadFirst, refreshKey]);

  /** Próxima página do scroll infinito — só as linhas (o back pula o COUNT). */
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const nextPage = page + 1;
    try {
      const res = await getHrAuditFeed(token, nextPage, PAGE_SIZE, search || undefined, dateFrom, dateTo, candidate);
      setEntries((prev) => [...prev, ...res.entries]);
      setPage(nextPage);
      // total vem -1 nas páginas > 1 (sem COUNT): usa heurística de página cheia.
      const more = res.entries.length >= PAGE_SIZE;
      setHasMore(more);
      hasMoreRef.current = more;
    } catch {
      setHasMore(false);
      hasMoreRef.current = false;
    } finally {
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [token, page, search, dateFrom, dateTo, candidate]);

  // Scroll infinito via sentinela + IntersectionObserver (estilo logs do Cloud Run):
  // dispara loadMore quando o fim da lista fica visível — mesmo se ainda não rolou.
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;
    const observer = new IntersectionObserver(
      (observed) => { if (observed[0].isIntersecting) void loadMore(); },
      { root, rootMargin: "120px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, loading]);

  return (
    <Box>
      {/* ── Cabeçalho ─────────────────────────────────────────────── */}
      <Flex align="center" justify="space-between" gap={3} mb={5}>
        <HStack spacing={3}>
          <Flex
            w="40px" h="40px" borderRadius="lg" flexShrink={0}
            bg={headerIconBg} color={headerIconClr}
            alignItems="center" justifyContent="center"
          >
            <History size={20} strokeWidth={2} />
          </Flex>
          <Box>
            <Text fontSize="lg" fontWeight="bold" color={heading}>Histórico de Alterações</Text>
            <Text fontSize="xs" color={muted}>
              Alterações em dados sensíveis dos processos internos de RH
            </Text>
          </Box>
        </HStack>

        {total > 0 && (
          <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={3} py={1} flexShrink={0}>
            {total} {total === 1 ? "registro" : "registros"}
          </Badge>
        )}
      </Flex>

      {/* ── Corpo (scroll infinito) ──────────────────────────────── */}
      {loading ? (
        <Flex justify="center" py={16}>
          <OnflyLoading containerSize={80} />
        </Flex>
      ) : entries.length === 0 ? (
        <Flex direction="column" align="center" justify="center" textAlign="center" py={16} gap={4}>
          <Flex
            w="64px" h="64px" borderRadius="full" bg={emptyBg} color={emptyClr}
            alignItems="center" justifyContent="center"
          >
            <Inbox size={28} strokeWidth={2} />
          </Flex>
          <Box>
            <Text fontSize="md" fontWeight="semibold" color={heading} mb={1}>
              Nenhum registro encontrado
            </Text>
            <Text fontSize="sm" color={muted} maxW="360px">
              {hasFilters
                ? "Tente ajustar a busca, o período ou o candidato nos filtros."
                : "As alterações realizadas nos processos de RH aparecerão aqui."}
            </Text>
          </Box>
        </Flex>
      ) : (
        <Box
          ref={scrollRef}
          maxH={{ base: "58vh", md: "calc(100vh - 300px)" }}
          overflowY="auto"
          pr={2}
          sx={{ scrollbarWidth: "thin", "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-thumb": { background: lineColor, borderRadius: "24px" } }}
        >
          <VStack align="stretch" spacing={0} animation={`${fadeUp} 0.25s ease-out`}>
            {entries.map((entry, i) => (
              <AuditTimelineItem
                key={entry.id}
                entry={entry}
                isFirst={i === 0}
                isLast={i === entries.length - 1}
                toneColors={toneColors}
                surfaceBg={surfaceBg}
                cardBg={cardBg}
                cardBorder={cardBorder}
                cardHoverBg={cardHoverBg}
                cardHoverBdr={cardHoverBdr}
                nodeRing={nodeRing}
                lineColor={lineColor}
                heading={heading}
                muted={muted}
                label={label}
                accent={accent}
                candChipBg={candChipBg}
                candChipClr={candChipClr}
              />
            ))}
          </VStack>

          {/* Sentinela do scroll infinito */}
          <Box ref={sentinelRef} h="1px" />

          {loadingMore && (
            <Flex justify="center" py={4}>
              <Spinner size="sm" color={muted} />
            </Flex>
          )}
          {!hasMore && !loadingMore && (
            <Text fontSize="xs" color={muted} textAlign="center" py={4}>
              Fim do histórico
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};

// ─── Item da timeline ─────────────────────────────────────────────────────────

interface TimelineItemProps {
  entry: HrProcessAuditEntry;
  isFirst: boolean;
  isLast: boolean;
  toneColors: Record<Tone, { bg: string; icon: string }>;
  surfaceBg: string;
  cardBg: string;
  cardBorder: string;
  cardHoverBg: string;
  cardHoverBdr: string;
  nodeRing: string;
  lineColor: string;
  heading: string;
  muted: string;
  label: string;
  accent: string;
  candChipBg: string;
  candChipClr: string;
}

const AuditTimelineItem = ({
  entry, isFirst, isLast, toneColors, surfaceBg, cardBg, cardBorder, cardHoverBg, cardHoverBdr, nodeRing, lineColor,
  heading, muted, label, accent, candChipBg, candChipClr,
}: TimelineItemProps) => {
  const parts = describeAuditParts(entry);
  const { Icon, tone } = getAuditVisual(parts.fieldKey, parts.changeType, parts.rawNew);
  const colors = toneColors[tone];
  const candidate = candidateDisplayName(entry);
  const showValues = parts.changeType !== "cleared";

  // Linha conectora: liga o nó atual ao próximo. Só existe quando há um próximo
  // item (o anterior já cobre a parte de cima). Primeiro item começa no centro do nó.
  const showLine = !isLast;

  return (
    <Flex position="relative" pb={isLast ? 0 : 5} gap={3} align="stretch">
      {/* Coluna do nó + linha conectora */}
      <Box w="36px" flexShrink={0} position="relative">
        {showLine && (
          <Box
            position="absolute"
            left="17px"
            w="2px"
            bg={lineColor}
            top={isFirst ? "18px" : "0"}
            bottom="0"
          />
        )}
        <Flex
          w="36px" h="36px" borderRadius="full"
          bg={surfaceBg} color={colors.icon} flexShrink={0} zIndex={1} position="relative"
          alignItems="center" justifyContent="center"
        >
          {/* Tint por cima da base opaca (mascara a linha conectora) */}
          <Box
            position="absolute" inset={0} borderRadius="full" bg={colors.bg}
            boxShadow={`inset 0 0 0 1px ${nodeRing}`}
          />
          <Icon size={18} strokeWidth={2} style={{ position: "relative", zIndex: 1 }} />
        </Flex>
      </Box>

      {/* Card de conteúdo */}
      <Box
        flex={1}
        minW={0}
        bg={cardBg}
        borderWidth="1px"
        borderColor={cardBorder}
        borderRadius="lg"
        px={4}
        py={3}
        transition="all 0.18s ease"
        _hover={{ shadow: "sm", borderColor: cardHoverBdr, bg: cardHoverBg }}
      >
        <Flex direction={{ base: "column", sm: "row" }} align={{ base: "flex-start", sm: "flex-start" }} justify={{ sm: "space-between" }} gap={{ base: 1, sm: 3 }}>
          <Box flex="1" minW="0">
            {/* Linha principal: ator • verbo • campo */}
            <Text fontSize="sm" noOfLines={2}>
              <Text as="span" fontWeight="semibold" color={heading}>{parts.actor}</Text>{" "}
              <Text as="span" color={muted}>{AUDIT_VERB[parts.changeType]}</Text>{" "}
              <Text as="span" fontWeight="medium" color={label}>{parts.field}</Text>
            </Text>

            {/* Linha de diff dos valores */}
            {showValues && (
              <Text fontSize="sm" mt={1} color={muted} noOfLines={2}>
                {parts.changeType === "added" && (
                  <>como <Text as="span" fontWeight="semibold" color={accent}>{parts.newValue}</Text></>
                )}
                {parts.changeType === "changed" && (
                  <>de <Text as="span" color={muted}>{parts.oldValue}</Text> para <Text as="span" fontWeight="semibold" color={accent}>{parts.newValue}</Text></>
                )}
                {parts.changeType === "removed" && (
                  <>valor anterior: <Text as="span" color={muted}>{parts.oldValue}</Text></>
                )}
              </Text>
            )}

            {/* Candidato destacado */}
            <HStack spacing={1.5} mt={2} color={muted} align="center">
              <User size={13} strokeWidth={2} />
              <Text fontSize="xs" fontWeight="medium" whiteSpace="nowrap">Candidato:</Text>
              <Box
                as="span"
                display="inline-flex"
                alignItems="center"
                bg={candChipBg}
                color={candChipClr}
                borderRadius="full"
                pl={2}
                pr={2.5}
                py={0.5}
                fontSize="xs"
                fontWeight="medium"
                maxW="100%"
                overflow="hidden"
              >
                <Text isTruncated>{candidate}</Text>
              </Box>
            </HStack>
          </Box>

          {/* Data/hora — discreta, à direita no desktop */}
          <Text
            fontSize="xs"
            color={muted}
            whiteSpace="nowrap"
            textAlign={{ base: "left", sm: "right" }}
            pt={{ base: 0, sm: 0.5 }}
            flexShrink={0}
          >
            {formatDateTime(entry.created_at)}
          </Text>
        </Flex>
      </Box>
    </Flex>
  );
};